/**
 * App controls: the admin-tunable dials (shared `app-settings`). The server
 * validates every save against the same schema, so this form only has to be
 * helpful, not authoritative.
 */

import { useEffect, useMemo, useState } from 'react';
import { Plus, RotateCcw, X } from 'lucide-react';
import { RELATIONSHIP_TIERS, TIER_LABELS, type AppSettings, type RelationshipTier } from '@toiyp/shared';
import { useAppDispatch } from '../../store/hooks';
import { appSettingsApi, zoneLabel } from '../settings/api';
import { useAdminSettingsQuery, useSaveAdminSettingsMutation } from './api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type NumberKey =
  | 'minHoldMinutes'
  | 'maxHoldHours'
  | 'defaultHoldHours'
  | 'minLetterChars'
  | 'nudgeMaxChars'
  | 'minDistinctWords'
  | 'maxPasteChars'
  | 'birthdayRemindDaysBefore'
  | 'firstClassFactor'
  | 'expressHoldShare'
  | 'expressMaxHoldMinutes'
  | 'expressMonthlyAllowance'
  | 'expressMaxBalance'
  | 'expressMinEngagement'
  | 'lastingLinkDailyCap'
  | 'referralMonthlyRewardLimit'
  | 'plusGiftsPerMonth';

const NUMBER_FIELDS: { key: NumberKey; label: string; unit: string; help: string; min: number; step?: number }[] = [
  {
    key: 'minHoldMinutes',
    label: 'Shortest delivery time',
    unit: 'minutes',
    help: 'After collection, no letter arrives sooner than this. Can’t go below 20.',
    min: 20,
  },
  {
    key: 'maxHoldHours',
    label: 'Longest delivery time',
    unit: 'hours',
    help: 'After collection, no letter is held longer than this.',
    min: 1,
    step: 0.5,
  },
  {
    key: 'defaultHoldHours',
    label: 'First-letters delivery time',
    unit: 'hours',
    help: 'Used until a pair has a rhythm of their own. Must sit between the two above.',
    min: 0,
    step: 0.5,
  },
  {
    key: 'minLetterChars',
    label: 'Minimum letter length',
    unit: 'characters',
    help: 'A letter can’t be sealed until it’s this long. Drafts are never blocked.',
    min: 0,
  },
  {
    key: 'nudgeMaxChars',
    label: 'Longest nudge',
    unit: 'characters',
    help: 'The P.S. a sender can add to an unanswered letter.',
    min: 10,
  },
  {
    key: 'maxPasteChars',
    label: 'Longest paste',
    unit: 'characters',
    help: 'The composer turns back a bigger paste with a gentle note. 0 allows any paste.',
    min: 0,
  },
  {
    key: 'minDistinctWords',
    label: 'Different words needed',
    unit: 'words',
    help: 'Part of the quality check: a letter needs at least this many different words. 0 skips it.',
    min: 0,
  },
];

/** Birthday reminders — its own dial, so it is not swept up in the slices above. */
const BIRTHDAY_FIELDS: (Omit<(typeof NUMBER_FIELDS)[number], 'key'> & { key: NumberKey })[] = [
  {
    key: 'birthdayRemindDaysBefore',
    label: 'Birthday reminder',
    unit: 'days ahead',
    help: 'How many days before a birthday their correspondents are reminded to write. 0 turns the reminder off.',
    min: 0,
  },
];

/** Referrals (slice 4): the lasting link's daily cap and the welcome-stamp limit. */
const REFERRAL_FIELDS: (Omit<(typeof NUMBER_FIELDS)[number], 'key'> & { key: NumberKey })[] = [
  {
    key: 'lastingLinkDailyCap',
    label: 'Lasting link, new people a day',
    unit: 'people',
    help: 'How many new pairs one member’s QR code, story card or bio link can start in a day. Past it, visitors are asked to come back tomorrow.',
    min: 1,
  },
  {
    key: 'referralMonthlyRewardLimit',
    label: 'Welcome stamps a month',
    unit: 'per inviter',
    help: 'The most welcome stamps one member can earn for bringing people in, per calendar month. 0 stops new ones.',
    min: 0,
  },
  {
    key: 'plusGiftsPerMonth',
    label: 'Stamp books a Plus member can gift',
    unit: 'a month',
    help: 'How many stamp books one toiyp Plus member can give, per calendar month. Admin grants don’t count. 0 stops new gifts.',
    min: 0,
  },
];

/** Postage dials (docs/POSTAGE_PLAN.md §4.4). Kept apart so the slices above stay put. */
const POSTAGE_FIELDS: (Omit<(typeof NUMBER_FIELDS)[number], 'key'> & { key: NumberKey })[] = [
  {
    key: 'firstClassFactor',
    label: '1st class speed',
    unit: '× the pace hold',
    help: 'Replaces the tier dial on a 1st-class letter. Lower is faster; 2nd class is 1.',
    min: 0.25,
    step: 0.05,
  },
  {
    key: 'expressHoldShare',
    label: 'Express speed',
    unit: '× the 2nd-class hold',
    help: 'An Express letter is held for this share of what 2nd class would take, from the moment it is sealed.',
    min: 0.05,
    step: 0.05,
  },
  {
    key: 'expressMaxHoldMinutes',
    label: 'Longest Express delivery',
    unit: 'minutes',
    help: 'No Express letter takes longer than this. Can’t go below 20.',
    min: 20,
  },
  {
    key: 'expressMonthlyAllowance',
    label: 'Express stamps a month',
    unit: 'stamps',
    help: 'Each member of a pair that has earned Express gets this many, once a month.',
    min: 0,
  },
  {
    key: 'expressMaxBalance',
    label: 'Most Express stamps held',
    unit: 'stamps',
    help: 'Earned stamps stop accruing at this many. At least the monthly allowance.',
    min: 0,
  },
  {
    key: 'expressMinEngagement',
    label: 'Engagement for Express',
    unit: '(0–1)',
    help: 'How engaged a pair must be, as well as the tier above. 0.7 is “flourishing”.',
    min: 0,
    step: 0.05,
  },
];

function same(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function errorText(e: unknown): string {
  const data = (e as { data?: { message?: string | string[] } })?.data;
  const msg = Array.isArray(data?.message) ? data!.message.join('; ') : data?.message;
  return msg ?? 'Couldn’t save the settings.';
}

export function AdminSettings() {
  const dispatch = useAppDispatch();
  const { data, error: loadError, isLoading } = useAdminSettingsQuery();
  const [save, { isLoading: saving }] = useSaveAdminSettingsMutation();
  const [form, setForm] = useState<AppSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setForm(data.values);
  }, [data]);

  const dirty = useMemo(() => !!form && !!data && !same(form, data.values), [form, data]);

  if (isLoading || !form || !data) {
    return <p className="admin__empty">{loadError ? 'Couldn’t load the settings.' : 'Loading…'}</p>;
  }
  const defaults = data.defaults;

  const set = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    setSaved(false);
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };

  const onSave = async () => {
    setError(null);
    try {
      await save(form).unwrap();
      // Everyone's compose counters and "last post" hints follow the new values.
      dispatch(appSettingsApi.util.invalidateTags(['AppSettings']));
      setSaved(true);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const Reset = ({ k }: { k: keyof AppSettings }) =>
    same(form[k], defaults[k]) ? null : (
      <button type="button" className="admin-setting__reset" onClick={() => set(k, defaults[k])}>
        <RotateCcw size={12} aria-hidden="true" /> default
      </button>
    );

  return (
    <div className="admin-settings">
      <section className="admin-settings__group">
        <h2 className="admin-settings__title">Delivery</h2>
        {NUMBER_FIELDS.slice(0, 3).map((f) => (
          <NumberRow key={f.key} field={f} value={form[f.key]} def={defaults[f.key]} onChange={(v) => set(f.key, v)}>
            <Reset k={f.key} />
          </NumberRow>
        ))}

        <div className="admin-setting">
          <div className="admin-setting__head">
            <span className="admin-setting__label">Collection times ({zoneLabel(form.postTimezone)})</span>
            <Reset k="collectionTimes" />
          </div>
          <p className="admin-setting__help">
            When the post van picks up sealed letters. Delivery time is counted from collection. Default:{' '}
            {defaults.collectionTimes.join(', ')}.
          </p>
          <div className="admin-setting__chips">
            {form.collectionTimes.map((t, i) => (
              <span key={i} className="admin-time">
                <input
                  type="time"
                  value={t}
                  aria-label={`Collection time ${i + 1}`}
                  onChange={(e) =>
                    set(
                      'collectionTimes',
                      form.collectionTimes.map((x, j) => (j === i ? e.target.value : x)),
                    )
                  }
                />
                {form.collectionTimes.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remove ${t}`}
                    onClick={() => set('collectionTimes', form.collectionTimes.filter((_, j) => j !== i))}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            ))}
            {form.collectionTimes.length < 12 && (
              <button
                type="button"
                className="admin-chip"
                onClick={() => set('collectionTimes', [...form.collectionTimes, '12:00'])}
              >
                <Plus size={12} aria-hidden="true" /> add time
              </button>
            )}
          </div>
        </div>

        <div className="admin-setting">
          <div className="admin-setting__head">
            <span className="admin-setting__label">Collection days</span>
            <Reset k="collectionDays" />
          </div>
          <p className="admin-setting__help">Days the post runs. A letter sealed on any other day waits for the next one.</p>
          <div className="admin-setting__chips">
            {DAYS.map((d, i) => {
              const on = form.collectionDays.includes(i);
              return (
                <button
                  key={d}
                  type="button"
                  className={`admin-chip${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() =>
                    set(
                      'collectionDays',
                      on ? form.collectionDays.filter((x) => x !== i) : [...form.collectionDays, i].sort(),
                    )
                  }
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>

        <div className="admin-setting">
          <div className="admin-setting__head">
            <label className="admin-setting__label" htmlFor="setting-postTimezone">
              Post timezone
            </label>
            <Reset k="postTimezone" />
          </div>
          <p className="admin-setting__help">
            Collection times and the delivery round are clock times in this zone, so they follow summer time (an IANA
            name, e.g. Europe/London). Default: {defaults.postTimezone}.
          </p>
          <div className="admin-setting__input">
            <input
              id="setting-postTimezone"
              className="admin-setting__zone"
              type="text"
              spellCheck={false}
              autoComplete="off"
              value={form.postTimezone}
              onChange={(e) => set('postTimezone', e.target.value.trim())}
            />
          </div>
        </div>

        <div className="admin-setting">
          <div className="admin-setting__head">
            <span className="admin-setting__label">Delivery round ({zoneLabel(form.postTimezone)})</span>
            <Reset k="deliveryStartTime" />
            <Reset k="deliveryEndTime" />
          </div>
          <p className="admin-setting__help">
            Letters are only delivered between these times, on the collection days above. A letter due outside the round
            waits for the next morning&rsquo;s. Default: {defaults.deliveryStartTime}–{defaults.deliveryEndTime}.
          </p>
          <div className="admin-setting__chips">
            <span className="admin-time">
              <input
                type="time"
                value={form.deliveryStartTime}
                aria-label="Delivery round starts"
                onChange={(e) => set('deliveryStartTime', e.target.value)}
              />
            </span>
            <span className="admin-setting__to">to</span>
            <span className="admin-time">
              <input
                type="time"
                value={form.deliveryEndTime}
                aria-label="Delivery round ends"
                onChange={(e) => set('deliveryEndTime', e.target.value)}
              />
            </span>
          </div>
        </div>
      </section>

      <section className="admin-settings__group">
        <h2 className="admin-settings__title">Letters</h2>
        {NUMBER_FIELDS.slice(3, 6).map((f) => (
          <NumberRow key={f.key} field={f} value={form[f.key]} def={defaults[f.key]} onChange={(v) => set(f.key, v)}>
            <Reset k={f.key} />
          </NumberRow>
        ))}

        <div className="admin-setting">
          <div className="admin-setting__head">
            <label className="admin-setting__label" htmlFor="setting-letterQualityChecks">
              Quality check
            </label>
            <Reset k="letterQualityChecks" />
          </div>
          <p className="admin-setting__help">
            Refuses letters that only reach the minimum by holding a key down, repeating themselves, or
            keyboard mashing. Turn off if it ever turns back real letters.
          </p>
          <div className="admin-setting__input">
            <input
              id="setting-letterQualityChecks"
              type="checkbox"
              checked={form.letterQualityChecks}
              onChange={(e) => set('letterQualityChecks', e.target.checked)}
            />
            <span>{form.letterQualityChecks ? 'On' : 'Off'}</span>
          </div>
        </div>
        {form.letterQualityChecks &&
          NUMBER_FIELDS.slice(6).map((f) => (
            <NumberRow key={f.key} field={f} value={form[f.key]} def={defaults[f.key]} onChange={(v) => set(f.key, v)}>
              <Reset k={f.key} />
            </NumberRow>
          ))}
        {BIRTHDAY_FIELDS.map((f) => (
          <NumberRow key={f.key} field={f} value={form[f.key]} def={defaults[f.key]} onChange={(v) => set(f.key, v)}>
            <Reset k={f.key} />
          </NumberRow>
        ))}
      </section>

      <section className="admin-settings__group">
        <h2 className="admin-settings__title">Referrals</h2>
        {REFERRAL_FIELDS.map((f) => (
          <NumberRow key={f.key} field={f} value={form[f.key]} def={defaults[f.key]} onChange={(v) => set(f.key, v)}>
            <Reset k={f.key} />
          </NumberRow>
        ))}
      </section>

      <section className="admin-settings__group">
        <h2 className="admin-settings__title">Postage</h2>
        <div className="admin-setting">
          <div className="admin-setting__head">
            <label className="admin-setting__label" htmlFor="setting-postageEnabled">
              Postage classes
            </label>
            <Reset k="postageEnabled" />
          </div>
          <p className="admin-setting__help">
            2nd class, 1st class and Express. Off, every letter goes exactly as it did before and the composer shows no
            stamp. 2nd class is always free.
          </p>
          <div className="admin-setting__input">
            <input
              id="setting-postageEnabled"
              type="checkbox"
              checked={form.postageEnabled}
              onChange={(e) => set('postageEnabled', e.target.checked)}
            />
            <span>{form.postageEnabled ? 'On' : 'Off'}</span>
          </div>
        </div>
        <TierRow
          id="firstClassMinTier"
          label="Tier that earns 1st class"
          value={form.firstClassMinTier}
          def={defaults.firstClassMinTier}
          onChange={(v) => set('firstClassMinTier', v)}
        >
          <Reset k="firstClassMinTier" />
        </TierRow>
        <TierRow
          id="expressMinTier"
          label="Tier that earns Express"
          value={form.expressMinTier}
          def={defaults.expressMinTier}
          onChange={(v) => set('expressMinTier', v)}
        >
          <Reset k="expressMinTier" />
        </TierRow>
        {POSTAGE_FIELDS.map((f) => (
          <NumberRow key={f.key} field={f} value={form[f.key]} def={defaults[f.key]} onChange={(v) => set(f.key, v)}>
            <Reset k={f.key} />
          </NumberRow>
        ))}
      </section>

      <div className="admin-settings__bar">
        <span className="admin-settings__meta">
          {error ? (
            <span className="admin-settings__error">{error}</span>
          ) : saved && !dirty ? (
            'Saved. New letters use these values now.'
          ) : data.updatedAt ? (
            `Last changed ${new Date(data.updatedAt).toLocaleString()}${data.updatedBy ? ` by ${data.updatedBy}` : ''}`
          ) : (
            'Using the shipped defaults.'
          )}
        </span>
        <button type="button" className="btn btn--ghost" disabled={!dirty || saving} onClick={() => setForm(data.values)}>
          Discard
        </button>
        <button type="button" className="btn btn--primary" disabled={!dirty || saving} onClick={() => void onSave()}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function NumberRow({
  field,
  value,
  def,
  onChange,
  children,
}: {
  field: (typeof NUMBER_FIELDS)[number];
  value: number;
  def: number;
  onChange: (v: number) => void;
  children?: React.ReactNode;
}) {
  const id = `setting-${field.key}`;
  return (
    <div className="admin-setting">
      <div className="admin-setting__head">
        <label className="admin-setting__label" htmlFor={id}>
          {field.label}
        </label>
        {children}
      </div>
      <p className="admin-setting__help">
        {field.help} Default: {def} {field.unit}.
      </p>
      <div className="admin-setting__input">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={field.min}
          step={field.step ?? 1}
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))}
        />
        <span>{field.unit}</span>
      </div>
    </div>
  );
}

/** A tier threshold — this tier and every warmer one qualify. */
function TierRow({
  id,
  label,
  value,
  def,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: RelationshipTier;
  def: RelationshipTier;
  onChange: (v: RelationshipTier) => void;
  children?: React.ReactNode;
}) {
  const htmlId = `setting-${id}`;
  return (
    <div className="admin-setting">
      <div className="admin-setting__head">
        <label className="admin-setting__label" htmlFor={htmlId}>
          {label}
        </label>
        {children}
      </div>
      <p className="admin-setting__help">
        This tier and every closer one qualify. Default: {TIER_LABELS[def]}.
      </p>
      <div className="admin-setting__input">
        <select id={htmlId} value={value} onChange={(e) => onChange(e.target.value as RelationshipTier)}>
          {RELATIONSHIP_TIERS.map((t) => (
            <option key={t} value={t}>
              {TIER_LABELS[t]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
